import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useBrandBranding } from "@/hooks/use-brand-branding";
import { effectiveProfile, profileCompleteness } from "@/lib/brand-profile";
import type { ProfileOverrides } from "@/lib/brand-profile";

// brands.profile_overrides is newer than the generated types, so these go untyped.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

function useBrandId() {
  const { data } = useBrandBranding();
  return data?.brandId ?? null;
}

/**
 * The brand's own corrections to its site analysis. A failed read (for example before the migration
 * is run) gives no corrections instead of breaking the page.
 */
export function useProfileOverrides() {
  const brandId = useBrandId();
  return useQuery({
    queryKey: ["brand-profile-overrides", brandId],
    enabled: !!brandId,
    queryFn: async (): Promise<ProfileOverrides> => {
      const { data, error } = await db.from("brands").select("profile_overrides").eq("id", brandId).maybeSingle();
      if (error) return {};
      return (data?.profile_overrides ?? {}) as ProfileOverrides;
    },
  });
}

export function useSaveOverrides() {
  const qc = useQueryClient();
  const brandId = useBrandId();
  const { data: current } = useProfileOverrides();
  return useMutation({
    mutationFn: async (patch: ProfileOverrides) => {
      if (!brandId) throw new Error("No brand on this account");
      const next = { ...(current ?? {}), ...patch };
      const { error } = await db.from("brands").update({ profile_overrides: next }).eq("id", brandId);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["brand-profile-overrides"] }),
  });
}

export function useSaveCommunityActivity() {
  const qc = useQueryClient();
  const brandId = useBrandId();
  return useMutation({
    mutationFn: async (text: string) => {
      if (!brandId) throw new Error("No brand on this account");
      const { error } = await supabase.from("brands").update({ community_activity: text.trim() || null }).eq("id", brandId);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["brand-branding"] }),
  });
}

/** Age range and gender focus, stored the way the onboarding stores them. */
export function useSaveAudience() {
  const qc = useQueryClient();
  const brandId = useBrandId();
  return useMutation({
    mutationFn: async (input: { ageMin: string | null; ageMax: string | null; genderFocus: string | null }) => {
      if (!brandId) throw new Error("No brand on this account");
      const { error: delErr } = await supabase.from("brand_audience_ages").delete().eq("brand_id", brandId);
      if (delErr) throw delErr;
      const rows = [
        input.ageMin && { brand_id: brandId, age_group: input.ageMin, priority: "min" },
        input.ageMax && { brand_id: brandId, age_group: input.ageMax, priority: "max" },
      ].filter(Boolean) as { brand_id: string; age_group: string; priority: string }[];
      if (rows.length) {
        const { error } = await supabase.from("brand_audience_ages").insert(rows);
        if (error) throw error;
      }
      const { error: gErr } = await supabase.from("brands").update({ gender_focus: input.genderFocus }).eq("id", brandId);
      if (gErr) throw gErr;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["brand-branding"] }),
  });
}

/** Ranked brand values: first in the list is rank 1. */
export function useSaveValues() {
  const qc = useQueryClient();
  const brandId = useBrandId();
  return useMutation({
    mutationFn: async (values: string[]) => {
      if (!brandId) throw new Error("No brand on this account");
      const { error: delErr } = await supabase.from("brand_values").delete().eq("brand_id", brandId);
      if (delErr) throw delErr;
      if (values.length) {
        const { error } = await supabase.from("brand_values").insert(values.map((value, i) => ({ brand_id: brandId, value, rank: i + 1 })));
        if (error) throw error;
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["brand-branding"] }),
  });
}

/** The brand's industry, stored the way the onboarding stores it. */
export function useSaveIndustry() {
  const qc = useQueryClient();
  const brandId = useBrandId();
  return useMutation({
    mutationFn: async (industry: string | null) => {
      if (!brandId) throw new Error("No brand on this account");
      const { error: delErr } = await supabase.from("brand_industries").delete().eq("brand_id", brandId);
      if (delErr) throw delErr;
      if (industry) {
        const { error } = await supabase.from("brand_industries").insert([{ brand_id: brandId, industry }]);
        if (error) throw error;
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["brand-branding"] }),
  });
}

/** How many briefs this brand has sent, in any stream. */
export function useBrandBriefCount() {
  const brandId = useBrandId();
  return useQuery({
    queryKey: ["briefs-count", brandId],
    enabled: !!brandId,
    queryFn: async () => {
      const { count, error } = await db.from("activation_briefs").select("id", { count: "exact", head: true }).eq("brand_id", brandId).neq("status", "cancelled");
      if (error) return 0;
      return (count ?? 0) as number;
    },
  });
}

/** How complete the brand's profile is, from the same data the profile page shows. */
export function useProfileCompleteness() {
  const { data: branding } = useBrandBranding();
  const { data: overrides } = useProfileOverrides();
  if (!branding) return { percent: 0, missing: [] as ReturnType<typeof profileCompleteness>["missing"] };
  const eff = effectiveProfile(branding.analysis, branding.summary, overrides ?? {});
  const { percent, missing } = profileCompleteness({
    website: branding.website,
    profile: eff,
    industries: branding.dna.industries,
    valuesCount: branding.dna.valuesRanked.length,
    ageMin: branding.dna.ageMin,
    genderFocus: branding.dna.genderFocus,
    communityActivity: branding.communityActivity,
  });
  return { percent, missing };
}
